// Section 8 and 13: Guard. 1 safety point per productive guard (spear 1.3x, a watch post 1.5x);
// 0.05 extra tool wear; requires strength 40+; 3% injury; heavy job. Gentle people will not serve
// (Section 5: the trait's restricts hook and this job's eligible hook). A palisade counts only
// when manned by 2 guards (Section 9): then it adds config.threats.palisadeSafetyPoints (E1-03).
// Deterrence = 1 + 0.15 x safetyPoints as deterrence strength; crime is divided by it (Section 13,
// E1-02). Constants: config.jobs.guard, config.crime.deterrencePerSafetyPoint, config.threats.
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import { heavyJobDemand } from '../../core/content-helpers';
import { palisadeStrength, safetyPoints } from '../../core/queries';
import { defineJob } from '../../core/registry';
import type { Modifier } from '../../core/types';

export function guard(config: Config) {
  const c = config.jobs.guard;
  const demand = heavyJobDemand('guard', config.needs.heavyJobFoodSurcharge);

  return defineJob({
    id: 'guard',
    label: 'Guard',
    section: 'S8',
    heavy: true,
    output: { kind: 'service', service: 'safetyPoints' },
    baseYield: c.safetyPoints,
    toolFactor: c.toolFactor,
    toolWear: c.toolWear,
    risks: { injury: c.injury },
    riskIllness: null,
    requiresBuilding: null,
    minStrength: c.minStrength,
    modifierTargets: ['demand', 'service', 'deterrence'],
    modifierKeys: { demand: ['food'] },
    modifiers(ctx, target): readonly Modifier[] {
      switch (target.kind) {
        case 'demand':
          return demand(ctx, target);
        case 'service': {
          if (target.service !== 'safetyPoints') return [];
          const manned = palisadeStrength(ctx.cfg, ctx.state);
          return manned > 0
            ? [
                mod.add(
                  'building:palisade',
                  ctx.cfg.threats.palisadeSafetyPoints * manned,
                  'palisade, manned',
                ),
              ]
            : [];
        }
        case 'deterrence': {
          const points = safetyPoints(ctx.state);
          if (points <= 0) return [];
          const strength = 1 + ctx.cfg.crime.deterrencePerSafetyPoint * points;
          return [
            mod.mul('deterrence:safety', strength, `${Math.round(points * 10) / 10} safety points`),
          ];
        }
        default:
          return [];
      }
    },
    produce: (_ctx, _worker, output) =>
      output > 0 ? [{ kind: 'service', service: 'safetyPoints', amount: output }] : [],
    eligible: (_ctx, person) => !person.traits.includes('gentle'),
  });
}
